import agent from "../../agent/agent";
import dayjs from "dayjs";
import { buildAddressesUrlByTargetId, buildComplexUrl } from "../../utils/sectionNavigation";

export const TASK_REPORT_STATUS = {
  MATCHED: "matched",
  TASK_ONLY: "task_only",
  DAILY_ONLY: "daily_only",
  CONFLICT: "conflict",
};

export const TASK_REPORT_TYPE = {
  MATCHED: 1,
  TASK_ONLY: 2,
  DAILY_ONLY: 3,
  CONFLICT: 4,
};

export const TASK_REPORT_STATUS_OPTIONS = [
  { value: TASK_REPORT_STATUS.MATCHED, label: "Выставлено по заданию" },
  { value: TASK_REPORT_STATUS.TASK_ONLY, label: "Не выставлено по заданию" },
  { value: TASK_REPORT_STATUS.DAILY_ONLY, label: "Выставлено вне задания" },
  { value: TASK_REPORT_STATUS.CONFLICT, label: "Несколько выставлений на одной локации" },
];

export const TASK_REPORT_INIT_FILTER = {
  daily_date: null,
  status: [],
  page: 1,
  limit: 15,
};

// Отображаем пустые значения без “прочерка”
export const EMPTY_CELL = "";

export const CONFLICT_PLACEHOLDER = {
  start_time: "Найдено",
  end_time: "несколько",
  complex_serial: "совпадений",
  grz_ts: "",
};

export const typeToStatus = (type) => {
  switch (Number(type)) {
    case TASK_REPORT_TYPE.MATCHED:
      return TASK_REPORT_STATUS.MATCHED;
    case TASK_REPORT_TYPE.TASK_ONLY:
      return TASK_REPORT_STATUS.TASK_ONLY;
    case TASK_REPORT_TYPE.DAILY_ONLY:
      return TASK_REPORT_STATUS.DAILY_ONLY;
    case TASK_REPORT_TYPE.CONFLICT:
      return TASK_REPORT_STATUS.CONFLICT;
    default:
      return "";
  }
};

export const parsePaginationTotal = (response) =>
  Number.parseFloat(String(response?.headers?.["x-pagination-total-count"] || 0).match(/\d+/g)) || 0;

export const normalizeTaskReportLocation = (location) => {
  if (!location || typeof location !== "object") return null;
  const id = String(location.address_id ?? location.id ?? "").trim();
  if (!id) return null;
  const name = String(location.address ?? location.name ?? "").trim();
  return { id, name };
};

export const normalizeTaskReportDaily = (daily) => {
  const src = daily && typeof daily === "object" ? daily : {};
  const addressId = String(src.address_id ?? "").trim();
  return {
    daily_id: String(src.daily_id ?? src.id ?? ""),
    complex_id: String(src.complex_id ?? ""),
    complex_serial: String(src.complex_serial ?? src.serial ?? ""),
    start_time: String(src.start_time ?? ""),
    end_time: String(src.end_time ?? ""),
    grz_ts: String(src.grz_ts ?? src.grz ?? ""),
    address_id: addressId,
    // Имя только для отображения; не используем текстовые поля как замену address_id
    address_name: addressId
      ? String(src.address_name ?? src.address ?? "").trim()
      : "",
  };
};

export const normalizeTaskReportItem = (item) => {
  const src = item && typeof item === "object" ? item : {};
  const taskData = src.task_data && typeof src.task_data === "object" ? src.task_data : {};
  const matchedLocations = (Array.isArray(taskData.matched_locations) ? taskData.matched_locations : [])
    .map(normalizeTaskReportLocation)
    .filter(Boolean);
  const dailyData = (Array.isArray(src.daily_data) ? src.daily_data : []).map(normalizeTaskReportDaily);

  return {
    id: String(src.id ?? ""),
    task_id: String(src.task_id ?? ""),
    contract_id: String(src.contract_id ?? ""),
    daily_date: src.daily_date ?? null,
    type: Number(src.type) || 0,
    status: typeToStatus(src.type),
    task_row_id: String(src.task_row_id ?? ""),
    task_data: {
      road_name: String(taskData.road_name ?? ""),
      fixation_point: String(taskData.fixation_point ?? ""),
      note: String(taskData.note ?? ""),
      matched_locations: matchedLocations,
    },
    daily_data: dailyData,
  };
};

export const getLatestDailyDate = (items = []) => {
  let latest = null;
  let latestTs = -Infinity;

  (Array.isArray(items) ? items : []).forEach((item) => {
    const value = item?.daily_date;
    if (!value) return;
    const ts = new Date(value).getTime();
    if (Number.isNaN(ts) || ts <= latestTs) return;
    latestTs = ts;
    latest = value;
  });

  return latest;
};

/** Probe without daily_date uses this page size to discover the latest day. */
export const TASK_REPORT_PROBE_LIMIT = 1000;

/**
 * Whether the probe response can be shown as-is for the resolved day
 * (avoids a second /tasks/report call). Refetch when dates are mixed
 * or the probe page is full and total says there is more data.
 */
export const canReuseLatestDailyProbe = (items = [], latestDailyDate, total = 0) => {
  const dayKey = toDailyDateParam(latestDailyDate);
  if (!dayKey) return false;

  const list = Array.isArray(items) ? items : [];
  if (!list.length) return false;

  const allSameDay = list.every((item) => toDailyDateParam(item?.daily_date) === dayKey);
  if (!allSameDay) return false;

  if (list.length >= TASK_REPORT_PROBE_LIMIT && Number(total) > list.length) {
    return false;
  }

  return true;
};

export const normalizeTaskReportMeta = (payload, items = []) => {
  const meta = payload?.meta ?? payload?.task ?? {};
  const first = items[0] || {};
  const taskId = String(meta.task_id ?? meta.id ?? first.task_id ?? "").trim();
  const letterNumber = String(meta.letter_number ?? meta.task_number ?? meta.number ?? "").trim();
  const letterDate = meta.letter_date ?? meta.task_date ?? null;
  const startDate = meta.start_date ?? meta.valid_from ?? meta.active_from ?? null;
  const documentId = String(meta.document_id ?? "").trim();
  const pdfUrl = String(meta.url_pdf ?? meta.pdf_url ?? meta.document_url ?? "").trim();
  const taskName = String(meta.name ?? meta.task_name ?? "").trim();
  const periods = Array.isArray(meta.periods)
    ? meta.periods
    : Array.isArray(payload?.periods)
      ? payload.periods
      : [];

  return {
    task_id: taskId,
    letter_number: letterNumber,
    letter_date: letterDate,
    start_date: startDate,
    document_id: documentId,
    pdf_url: pdfUrl,
    task_name: taskName,
    periods,
    daily_date: payload?.daily_date ?? getLatestDailyDate(items) ?? null,
  };
};

export const normalizeTaskReportResponse = (response) => {
  const payload = response?.data;
  const list = payload?.data ?? payload ?? [];
  const items = (Array.isArray(list) ? list : []).map(normalizeTaskReportItem);
  return {
    items,
    meta: normalizeTaskReportMeta(payload, items),
    total: parsePaginationTotal(response) || items.length,
  };
};

export const buildTaskReportQuery = (filter, contractId) => {
  const params = new URLSearchParams();
  if (contractId) params.set("contract_id", String(contractId));
  if (filter?.daily_date) params.set("daily_date", filter.daily_date);
  // status фильтруем на клиенте: бэкенд по status часто отдаёт пустой список
  params.set("page", String(filter?.page || 1));
  params.set("limit", String(filter?.limit || 15));
  return params.toString();
};

export const filterTaskReportItemsByStatus = (items = [], statuses = []) => {
  if (!Array.isArray(statuses) || !statuses.length) return items;
  const allowed = new Set(statuses.filter(Boolean));
  return (items || []).filter((item) => allowed.has(item?.status));
};

export const paginateItems = (items = [], page = 1, limit = 15) => {
  const safePage = Math.max(1, Number(page) || 1);
  const safeLimit = Math.max(1, Number(limit) || 15);
  const start = (safePage - 1) * safeLimit;
  return items.slice(start, start + safeLimit);
};


export const formatTaskDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

export const formatTaskInfoLabel = (meta) => {
  if (!meta) return "";
  const startDate = formatTaskDate(meta.start_date);

  // Backend already formats title as "Задание №2 от 07.07.2026"
  if (meta.task_name) {
    return startDate ? `${meta.task_name} действует с ${startDate}` : meta.task_name;
  }

  const number = meta.letter_number;
  const letterDate = formatTaskDate(meta.letter_date);
  if (!number && !letterDate && !startDate) return "";

  const parts = [];
  if (number) {
    parts.push(letterDate ? `Задание №${number} от ${letterDate}` : `Задание №${number}`);
  } else if (letterDate) {
    parts.push(`Задание от ${letterDate}`);
  } else {
    parts.push("Задание");
  }
  if (startDate) parts.push(`действует с ${startDate}`);
  return parts.join(" ");
};

export const toDailyDateParam = (isoOrDate) => {
  if (!isoOrDate) return null;
  const date = new Date(isoOrDate);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
};

export const normalizePeriod = (period) => {
  if (!period || typeof period !== "object") return null;
  const start = dayjs(period.start ?? period.from ?? period.start_date ?? period.begin);
  if (!start.isValid()) return null;
  const endRaw = period.end ?? period.to ?? period.end_date ?? period.finish ?? null;
  const end = endRaw ? dayjs(endRaw) : null;
  return {
    start: start.startOf("day"),
    end: end && end.isValid() ? end.startOf("day") : null,
    task_id: String(period.task_id ?? period.id ?? "").trim(),
  };
};

export const normalizePeriods = (periods) =>
  (Array.isArray(periods) ? periods : []).map(normalizePeriod).filter(Boolean);

export const buildPeriodsFromTaskStarts = (tasks = []) => {
  const starts = (Array.isArray(tasks) ? tasks : [])
    .map((task) => {
      const start = dayjs(task?.start_date ?? task?.valid_from ?? task?.active_from);
      if (!start.isValid()) return null;
      return {
        task_id: String(task?.id ?? "").trim(),
        start: start.startOf("day"),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  return starts.map((task, index) => {
    const next = starts[index + 1];
    return {
      start: task.start,
      end: next ? next.start.subtract(1, "day").startOf("day") : null,
      task_id: task.task_id,
    };
  });
};

export const extractContractTasks = (response, contractId) => {
  const payload = response?.data?.data ?? response?.data ?? [];
  const list = Array.isArray(payload) ? payload : [];
  const contractKey = String(contractId || "").trim();

  // Grouped by contract: [{ contract_id, tasks: [...] }]
  if (list.some((item) => Array.isArray(item?.tasks))) {
    const matched = list.filter((group) => {
      if (!contractKey) return true;
      return String(group?.contract_id ?? "") === contractKey;
    });
    return matched.flatMap((group) => (Array.isArray(group.tasks) ? group.tasks : []));
  }

  // Flat list of tasks
  if (!contractKey) return list;
  return list.filter((task) => String(task?.contract_id ?? "") === contractKey);
};

export const fetchContractTaskPeriods = async (contractId) => {
  if (!contractId) return [];
  const response = await agent
    .get(`/tasks?contract_id=${encodeURIComponent(String(contractId))}`)
    .catch(() => null);
  if (!response) return [];
  const tasks = extractContractTasks(response, contractId);
  return buildPeriodsFromTaskStarts(tasks);
};

export const isDateInPeriods = (date, periods = []) => {
  const day = dayjs(date);
  if (!day.isValid()) return false;
  return normalizePeriods(periods).some((period) => {
    if (day.isBefore(period.start, "day")) return false;
    if (period.end && day.isAfter(period.end, "day")) return false;
    return true;
  });
};

export const resolveTaskPeriods = ({ metaPeriods, contractPeriods, taskMeta }) => {
  const fromMeta = normalizePeriods(metaPeriods);
  if (fromMeta.length) return fromMeta;

  const fromContract = normalizePeriods(contractPeriods);
  if (fromContract.length) return fromContract;

  if (taskMeta?.start_date) {
    return buildPeriodsFromTaskStarts([{
      id: taskMeta.task_id,
      start_date: taskMeta.start_date,
    }]);
  }

  return [];
};


export const getTaskReportDailyLocation = (daily) => {
  if (!daily || typeof daily !== "object") return null;
  const id = String(daily.address_id ?? "").trim();
  // Без address_id строка не сопоставляется с адресом (CP-701 / несопоставленная)
  if (!id) return null;
  const name = String(daily.address_name ?? "").trim();
  return { id, name };
};

export const getTaskReportRowLocation = (item) => {
  const status = item?.status;
  if (status === TASK_REPORT_STATUS.TASK_ONLY || status === TASK_REPORT_STATUS.CONFLICT) {
    return null;
  }
  return getTaskReportDailyLocation(item?.daily_data?.[0] || null);
};

export const getAddressHref = (location) => {
  if (!location) return null;
  // Параметр тот же — target_id; значение берём из рабочего address_id.
  // Имя адреса — запасной ключ сайдпейджа, если id из сверки за эту дату уже не в справочнике.
  const targetId = String(location.id ?? location.address_id ?? "").trim();
  if (!targetId) return null;
  const name = String(location.name ?? location.address_name ?? "").trim();
  return buildAddressesUrlByTargetId(targetId, { location: name });
};

export const getComplexHref = (daily) => {
  if (!daily) return null;
  return buildComplexUrl({
    targetId: daily.complex_id,
    serial: daily.complex_serial,
  });
};

export const getDocumentsHref = (meta) => {
  if (!meta) return null;
  if (meta.document_id) return `/documents?document_id=${encodeURIComponent(meta.document_id)}&open=1`;
  if (meta.pdf_url) return `/documents?url=${encodeURIComponent(meta.pdf_url)}&open=1`;
  if (meta.task_id) return `/documents?task_id=${encodeURIComponent(meta.task_id)}&open=1`;
  return null;
};

export const normalizeTaskDetails = (response) => {
  const src = response?.data?.data ?? response?.data ?? {};
  const startDate = src.start_date ?? src.valid_from ?? src.active_from ?? null;

  return {
    task_id: String(src.id ?? ""),
    letter_number: String(src.letter_number ?? src.number ?? "").trim(),
    letter_date: src.letter_date ?? null,
    start_date: startDate,
    document_id: String(src.document_id ?? "").trim(),
    pdf_url: String(src.url_pdf ?? src.pdf_url ?? "").trim(),
    task_name: String(src.name ?? src.task_name ?? "").trim(),
  };
};

export const fetchTaskDetailsById = async (taskId) => {
  if (!taskId) return null;
  const response = await agent.get(`/tasks/${taskId}`).catch(() => null);
  if (!response) return null;
  return normalizeTaskDetails(response);
};

export const mergeTaskMetaWithDetails = (meta, details) => {
  if (!meta && !details) return null;
  return {
    ...(meta || {}),
    ...(details || {}),
    task_id: details?.task_id || meta?.task_id || "",
    letter_number: details?.letter_number || meta?.letter_number || "",
    letter_date: details?.letter_date || meta?.letter_date || null,
    start_date: details?.start_date || meta?.start_date || null,
    document_id: details?.document_id || meta?.document_id || "",
    pdf_url: details?.pdf_url || meta?.pdf_url || "",
    task_name: details?.task_name || meta?.task_name || "",
    daily_date: meta?.daily_date || null,
    periods: meta?.periods || [],
  };
};

